import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, EMPTY, finalize, Observable, Subject, tap } from 'rxjs';
import {
  GsStructPage,
  GsStructShoppingCartComponent,
  IAddItemResponse,
  IMethod,
  IResult,
  IShoppingCartItem,
  IShoppingCartMutationResponse,
  IShoppingCartRender,
  PictureDisplayMode,
  TransactionType
} from '../../models';
import { AuthProvider, CmsProvider, ComponentProvider } from '../../providers';
import { noop } from '../../utils/noop.util';
import { MethodsService } from '../methods/methods.service';
import { UrlResolverService } from '../url/url-resolver.service';

/** Idle time in ms before changes queued during a running cart request are sent. */
const QUEUED_CHANGES_DELAY = 500;

/** Url parameters of the add-to-basket method. */
interface IAddItemUrlParams {
  /** The article key that is added */
  id: number;
  /** The transaction type the item is added with */
  type: TransactionType;
  /** The requested quantity */
  quantity: number;
  /** Whether the item is removed instead of added */
  deleted: boolean;
  /** The chosen value of a voucher article */
  fixedPrice?: number;
  /** The shipping, delivery or pickup method preselected in the checkout */
  shippingMethodID?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartService {
  // Properties
  /** Current shopping-cart */
  shoppingCart: WritableSignal<IShoppingCartRender | undefined> = signal(undefined);
  /** Single result entry, set from sources that emit one message (e.g. immediate operation responses). */
  result: WritableSignal<IResult | undefined> = signal(undefined);
  /** Result array, set from sources that emit a list (cart render, batched responses, error payloads). */
  resultArray: WritableSignal<IResult[] | undefined> = signal(undefined);
  /**
   * Combined view of {@link result} and {@link resultArray}. Returns the
   * single result when only one is present, the array when only list results
   * are present, or a merged array when both sources are populated.
   *
   * This preserves compatibility with consumers that still expect the legacy
   * single-or-array contract while keeping the service values derived from the
   * two underlying signals.
   */
  results: Signal<IResult[]> = computed(() => {
    const single = this.result();
    const array = this.resultArray();

    if (single && array?.length) {
      return [single, ...array];
    }

    if (single) {
      return [single];
    }

    return array ?? [];
  });
  /** Indicates that an item was added to the cart recently (e.g. for sidebar feedback). */
  itemAdded = signal(false);
  /** Indicates that a cart operation (delete/update) is currently in progress. */
  processing = signal(false);
  /**
   * Backend component type used to (re)fetch the cart render. Defaults to the regular cart; the
   * inquiry cart overview sets this to `InquiryShoppingCart` so refreshes target its own endpoint
   * and `RequestList` basket. Set from `cmp.componentType` on every render, so it self-corrects per page.
   */
  componentType: WritableSignal<'ShoppingCart' | 'InquiryShoppingCart'> = signal('ShoppingCart');
  /**
   * Picture display mode of the "Warenkorb" component on the cart page. The cart panel has no
   * settings of its own and follows this one. Undefined until the cart page settings are known.
   */
  cartPagePictureDisplayMode: WritableSignal<PictureDisplayMode | undefined> = signal(undefined);
  /** Whether the settings of the cart page are known or already being loaded. */
  private _cartPageSettingsRequested = false;
  /** Internal timeout handle to auto-reset itemAdded flag. */
  private _itemAddedTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Latest requested item update per item id, queued while another cart operation is running. */
  private readonly _queuedItemUpdates: Map<number, IShoppingCartItem> = new Map();
  /** Latest requested transaction type per item id, queued while another cart operation is running. */
  private readonly _queuedTransactionTypes: Map<number, TransactionType> = new Map();
  /** Triggers sending of the queued changes, debounced so a burst of changes results in one request. */
  private readonly _flushQueued: Subject<void> = new Subject<void>();

  // References
  TransactionTypeEnum = TransactionType;

  // Services
  private readonly authService: AuthProvider = inject(AuthProvider);
  private readonly cmsProvider: CmsProvider = inject(CmsProvider);
  private readonly componentService: ComponentProvider = inject(ComponentProvider);
  private readonly destroyRef = inject(DestroyRef);
  private readonly methodsService: MethodsService = inject(MethodsService);
  private readonly urlResolver: UrlResolverService = inject(UrlResolverService);

  constructor() {
    // Wait for the changes to settle before sending what was queued during a running request,
    // otherwise every further change would go out back to back with the requests completing.
    this._flushQueued.pipe(debounceTime(QUEUED_CHANGES_DELAY), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.sendQueued());

    // Ensure we don't leave timers behind when the service gets destroyed (SSR / tests / edge cases).
    this.destroyRef.onDestroy(() => {
      if (this._itemAddedTimeout) {
        clearTimeout(this._itemAddedTimeout);
        this._itemAddedTimeout = null;
      }
    });
  }

  //Getters
  /** Retrieves the total number of items in the shopping cart. */
  get amount(): number {
    return this.authService.cookies(true).basket?.basketCount ?? 0;
  }

  // Methods
  /**
   * Call to the backend to get the current shoppingcart
   * @returns The current shopping cart
   */
  updateCart(): Observable<IShoppingCartRender> {
    return this.componentService.get<IShoppingCartRender, undefined>(this.componentType()).pipe(
      tap((response) => {
        this.shoppingCart.set({ ...response });
      })
    );
  }

  /**
   * Takes over the settings of the "Warenkorb" component on the cart page, so the cart panel follows them.
   * @param cmp The cart component rendered on the cart page
   */
  setCartPageSettings(cmp: GsStructShoppingCartComponent) {
    this._cartPageSettingsRequested = true;
    this.cartPagePictureDisplayMode.set(cmp.pictureDisplayMode);
  }

  /**
   * Loads the settings of the "Warenkorb" component from the cart page once, for the cart panel that is
   * shown outside of the cart page. Does nothing while the cart (and with it the cart page link) is not
   * loaded yet, or on the server, where the cart page url cannot be resolved.
   */
  loadCartPageSettings() {
    if (this._cartPageSettingsRequested) return;
    const url = this.urlResolver.resolveUrl(this.shoppingCart()?.linkTargetShoppingCart, 'frontend');
    if (!url) return;

    this._cartPageSettingsRequested = true;
    this.cmsProvider
      .getPageByUrl(url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          const cmp = this.findCartComponent(page);
          if (cmp) this.cartPagePictureDisplayMode.set(cmp.pictureDisplayMode);
        },
        error: noop
      });
  }

  /**
   * Finds the regular "Warenkorb" component on a page.
   * @param page The page to search
   * @returns The cart component, or undefined if the page has none
   */
  private findCartComponent(page: GsStructPage): GsStructShoppingCartComponent | undefined {
    return page.blocks
      ?.flatMap((block) => block.components ?? [])
      .find((cmp): cmp is GsStructShoppingCartComponent => cmp.componentType === 'ShoppingCart');
  }

  /**
   * Sets the itemAdded signal to true and resets it automatically after a short delay.
   * This is intended for simple UI feedback (e.g. sidebar "item added" hint).
   */
  private triggerItemAdded() {
    this.itemAdded.set(true);

    if (this._itemAddedTimeout) {
      clearTimeout(this._itemAddedTimeout);
      this._itemAddedTimeout = null;
    }

    this._itemAddedTimeout = setTimeout(() => {
      this.itemAdded.set(false);
      this._itemAddedTimeout = null;
    }, 3000);
  }

  /**
   * Call to the backend to update the item in the shoppingcart
   * @param item The item that should be updated
   * @returns void
   */
  updateItem(item: IShoppingCartItem) {
    // Queue instead of dropping, so a change made while a request runs is not lost.
    // Only the newest state per item is kept - it carries the absolute quantity anyway.
    if (this.processing()) {
      this._queuedItemUpdates.set(item.iD, item);
      return;
    }
    this.result.set(undefined);
    this.resultArray.set(undefined);
    const currentCart = this.shoppingCart();
    if (!currentCart?.methods.update) return;

    this.processing.set(true);
    this.methodsService
      .callMethod<IShoppingCartMutationResponse>(currentCart.methods.update, null, item, {
        id: item.iD?.toString(),
        type: item.transactionType,
        item
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          // Update results from the immediate response if available
          if (response?.results && response.results.length > 0) {
            this.resultArray.set(response.results);
          }

          // Refresh the cart to get the latest state (which will also update results)
          this.updateCart()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              complete: () => this.finishProcessing(),
              error: () => this.finishProcessing()
            });
        },
        error: (error) => {
          if (error.error?.results && error.error.results.length > 0) {
            this.resultArray.set(error.error.results);
          }
          this.finishProcessing();
        }
      });
  }

  /**
   * Marks the running cart operation as done and schedules the queued changes, if any.
   */
  private finishProcessing() {
    this.processing.set(false);

    if (this._queuedTransactionTypes.size > 0 || this._queuedItemUpdates.size > 0) this._flushQueued.next();
  }

  /**
   * Sends the longest waiting queued change, carrying its newest state. Further queued changes
   * follow once this one finished.
   */
  private sendQueued() {
    if (this.processing()) return; // A new request already started, its completion schedules the rest

    const [nextType] = this._queuedTransactionTypes.entries();
    if (nextType) {
      const [itemId, type] = nextType;
      this._queuedTransactionTypes.delete(itemId);
      this.setTransactionType(type, itemId);
      return;
    }

    const [nextItem] = this._queuedItemUpdates.values();
    if (!nextItem) return;
    this._queuedItemUpdates.delete(nextItem.iD);
    this.updateItem(nextItem);
  }

  /**
   * Sets the transaction type for a shopping cart item.
   * @param type The transaction type to set.
   * @param itemId The ID of the shopping cart item to update.
   */
  setTransactionType(type: TransactionType, itemId: number) {
    // Queue instead of dropping, see updateItem
    if (this.processing()) {
      this._queuedTransactionTypes.set(itemId, type);
      return;
    }
    const currentCart = this.shoppingCart();
    if (!currentCart?.methods?.setTransactionType) return;

    this.processing.set(true);
    this.methodsService
      .callMethod<TransactionType>(currentCart.methods.setTransactionType, { type }, null, { id: itemId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updateCart()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ complete: () => this.finishProcessing(), error: () => this.finishProcessing() });
        },
        error: () => {
          this.finishProcessing();
        }
      });
  }

  /** Call to the backend to delete the item from the shoppingcart */
  deleteItem(id: number) {
    if (this.processing()) return;
    const currentCart = this.shoppingCart();
    if (!currentCart?.methods.delete) return;

    this.processing.set(true);
    this.methodsService
      .callMethod<IShoppingCartMutationResponse>(currentCart.methods.delete, null, null, { id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updateCart()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ complete: () => this.finishProcessing(), error: () => this.finishProcessing() });
        },
        error: (error) => {
          console.error('Error deleting item from shopping cart:', error);
          if (error.error?.results && error.error.results.length > 0) {
            this.resultArray.set(error.error.results);
          }
          this.updateCart()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ complete: () => this.finishProcessing(), error: () => this.finishProcessing() });
        }
      });
  }

  /**
   * Ensures that the headers for the given method are set with the provided article ID and article key ID.
   * If the headers are already set, the method is returned as is.
   * @param method - The method object that may or may not have headers set.
   * @param articleID - The ID of the article to be included in the headers.
   * @param articleKeyID - The key ID of the article to be included in the headers.
   * @returns The method object with headers set if they were not already present.
   * @deprecated Do not set headers (articleID) manually anymore. The backend response is providing the corrrect method with headers.
   */
  protected ensureHeaders(method: IMethod, articleID: number): IMethod {
    if (method.headers) return method;
    console.warn('ShoppingCartService: ensureHeaders is deprecated and will be removed in future versions.');
    method.headers = {
      code: {
        ArticleID: articleID
      }
    };
    return method;
  }

  /**
   * Adds an item to the shopping cart or to the wish list depending on the method provided.
   * @param articleID - The ID of the article to add.
   * @param articleKeyID - The key ID of the article to add.
   * @param quantity - The quantity of the article to add.
   * @param method - The method to use for the request.
   * @param voucherValue - (Optional) The voucher value to apply to the article.
   * @param payloadVoucher - (Optional) Additional payload data for the voucher.
   * @param transactionType - (Optional) The type of transaction, defaults to TransactionType.SHIPPING.
   * @param deleted - (Optional) Indicates if the item is to be marked as deleted, defaults to false.
   * @param shippingMethodID - (Optional) The shipping, delivery or pickup method preselected in the checkout.
   * @returns A promise that resolves to the response from the method call.
   */
  addItem<T = IAddItemResponse>(
    articleID: number,
    articleKeyID: number,
    quantity: number,
    method: IMethod,
    voucherValue?: number,
    payloadVoucher?: Record<string, unknown>,
    transactionType: TransactionType = TransactionType.SHIPPING,
    deleted: boolean = false,
    shippingMethodID?: number
  ): Observable<T | undefined> {
    if (this.processing()) return EMPTY;

    const urlParams: IAddItemUrlParams = {
      id: articleKeyID,
      type: transactionType,
      quantity,
      deleted
    };

    if (voucherValue !== undefined) {
      urlParams.fixedPrice = voucherValue;
    }
    if (shippingMethodID != null) {
      urlParams.shippingMethodID = shippingMethodID;
    }

    this.processing.set(true);
    return this.methodsService.callMethod<T>(this.ensureHeaders(method, articleID), urlParams, payloadVoucher).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        this.triggerItemAdded();

        this.updateCart().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      }),
      finalize(() => this.finishProcessing())
    );
  }
}
